import React, { useState } from "react";
import { IForm } from "../../../../types/form.type";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../../../contexts/alert/AlertProvider";
import { Controller, useForm } from "react-hook-form";
import {
  asyncPostData,
  asyncPutData,
  uploadFile,
} from "../../../../utils/httpRequests";
import ModalForm from "../../modal/ModalForm";
import { Stack } from "@mui/material";
import TextInput from "../../input/TextInput";
import {
  formatDateDisplay,
  generateLinkImage,
} from "../../../../utils/helpers";
import ImageInput from "../../input/ImageInput";
import WrapperInput from "../../input/WrapperInput";
import NumberInput from "../../input/NumberInput";
import DateInput from "../../input/DateInput";
import SelectInput from "../../input/SelectInput";
import { BANNER_FORUM_POSTION_OPTIONS } from "../../../../utils/ui.constants";

const schema = yup.object({
  start_date: yup
    .date()
    .typeError("Vui lòng chọn ngày bắt đầu")
    .required("Vui lòng chọn ngày bắt đầu"),
  end_date: yup
    .date()
    .typeError("Vui lòng chọn ngày kết thúc")
    .required("Vui lòng chọn ngày kết thúc"),
});

function FormBannerForum({
  defaultValues,
  isEdit,
  apiCode,
  open,
  onClose,
  setLoad,
}: IForm) {
  const app = useSelector((state: any) => state.app.data);
  const token = useSelector((state: any) => state.auth.token);
  const { showAlert } = useAlertContext();
  const [picture, setPicture] = useState<File | undefined>();
  const defaultData: { [key: string]: any } = {
    stt: 1,
    tieu_de: "",
    hinh_anh: "",
    mo_ta: "",
    position: BANNER_FORUM_POSTION_OPTIONS[0].value,
    link: "",
    start_date: null,
    end_date: null,
  };
  const originData = defaultValues
    ? {
        ...defaultData,
        ...defaultValues,
        link: defaultValues?.exfields?.link || "",
        stt: defaultValues?.exfields?.stt || 0,
        position: defaultValues?.exfields?.position || "",
        start_date: defaultValues?.exfields?.start_date
          ? formatDateDisplay(defaultValues?.exfields?.start_date, "YYYY-MM-DD")
          : null,
        end_date: defaultValues?.exfields?.end_date
          ? formatDateDisplay(defaultValues?.exfields?.end_date, "YYYY-MM-DD")
          : null,
      }
    : defaultData;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: originData,
    resolver: yupResolver(schema),
  });

  const handleReset = () => {
    reset(originData);
  };

  const generateDataPost = async (values: typeof defaultData) => {
    const { link, stt, position, start_date, end_date, ...fields } = values;
    const result: { [key: string]: any } = {
      ...fields,
      exfields: {
        ...(defaultValues?.exfields || {}),
        type: "forum",
        link,
        position,
        stt,
        start_date,
        end_date,
      },
    };
    if (picture) {
      // save picture
      const formData = new FormData();
      formData.append("file", picture);
      const respFile = await uploadFile({
        formData,
        folder: "banner",
        token,
      });
      if (respFile && respFile.status === 200) {
        result.hinh_anh = respFile.data?.fileUrl;
        result.link = generateLinkImage(respFile.data?.fileUrl);
      }
    }
    return result;
  };

  const handleSave = async (values: typeof defaultData) => {
    const dataPost: { [key: string]: any } = await generateDataPost(values);
    let resp = null;
    if (isEdit) {
      resp = await asyncPutData({
        id: dataPost._id,
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    } else {
      resp = await asyncPostData({
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    }
    if (resp.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi lưu đối tượng",
      });
    } else {
      onClose();
      setLoad?.((prev: number) => prev + 1);
    }
  };

  const onSubmit = handleSubmit(handleSave);

  return (
    <ModalForm
      formWidth="600px"
      formTitle="Banner"
      loading={isSubmitting}
      open={open}
      isEdit={isEdit}
      onClose={onClose}
      onSubmit={onSubmit}
      onReset={handleReset}
    >
      <Stack gap={2}>
        <NumberInput
          label="STT"
          labelWidth="25%"
          placeholder="Nhập số thứ tự"
          name="stt"
          register={register}
        />
        <TextInput
          label="Tiêu đề"
          labelWidth="25%"
          placeholder="Nhập tiêu đề hình ảnh"
          name="tieu_de"
          register={register}
        />
        <Controller
          control={control}
          name="start_date"
          render={({ field: { value, onChange } }) => {
            return (
              <DateInput
                required
                label="Từ ngày"
                labelWidth="25%"
                value={value}
                onChange={onChange}
                errorMessage={errors?.start_date?.message as string}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="end_date"
          render={({ field: { value, onChange } }) => {
            return (
              <DateInput
                required
                label="Đến ngày"
                labelWidth="25%"
                value={value}
                onChange={onChange}
                errorMessage={errors?.end_date?.message as string}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="position"
          render={({ field: { value, onChange } }) => {
            return (
              <SelectInput
                label="Vị trí"
                labelWidth="25%"
                defaultValue={value}
                options={BANNER_FORUM_POSTION_OPTIONS}
                value={value}
                onChange={onChange}
              />
            );
          }}
        />
        <TextInput
          label="Liên kết"
          labelWidth="25%"
          placeholder="Nhập liên kết"
          name="link"
          register={register}
        />
        <TextInput
          multiline
          label="Ghi chú"
          labelWidth="25%"
          placeholder="Nhập ghi chú"
          name="mo_ta"
          register={register}
        />
        <WrapperInput direction="row" label="Hình ảnh (1:3)" labelWidth="25%">
          <ImageInput
            wrapperSx={{ width: "100%", height: "300px" }}
            url={
              defaultValues?.hinh_anh
                ? generateLinkImage(defaultValues?.hinh_anh)
                : ""
            }
            onChange={(file) => setPicture(file)}
          />
        </WrapperInput>
      </Stack>
    </ModalForm>
  );
}

export default FormBannerForum;
